import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation} from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import isEqual from 'lodash/isEqual';
import { takeWhile } from 'rxjs/operators';

import { Product } from '../../../../models/product.model';
import { Order } from '../../../../models/order.model';
import { OrderItem } from '../../../../models/order-item.model';
import { OrderItemService } from '../../../../services/order-item.service';
import { ProductService } from '../../../../services/product.service';
import { AuthService } from '../../../../services/auth.service';
import { MobileCheckServiceService } from '../../../../services/mobile-check-service.service';
import { OrderItemDelivery } from '../../../../models/order-item-delivery.model';
import {
  validateOrderItem,
  validateWbsForUnique,
} from '../../../../shared/helpers';
import { isEmptyArray } from '../../../../shared/utils';

import { SKU } from '../../../../models/sku.model';
import { Address } from 'app/models/address.model';
import { OrderItemSku } from 'app/models/order-item-sku.model';
import { PaymentService } from 'app/services/payments.service';
import { OrderService } from 'app/services/order.service';
import {ShippingMethod} from '../../../../models/shipping-method';
import cloneDeep from 'lodash/cloneDeep';
import {Currency} from '../../../../interfaces/currency';
import {ConfigService} from '../../../../services/config.service';
import {ECOMMERCE_TYPE} from '../../../../constants/order-types';

const DETAILED_ITEM = 'detailed';

@Component({
  selector: '[app-product-list-item]',
  templateUrl: './product-list-item.component.html',
  styleUrls: ['./product-list-item.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProductListItemComponent implements  OnInit, OnDestroy {
  shippingMethods: ShippingMethod[] = [];
  _orderItem: OrderItem;
  _originalOrderItem: OrderItem;
  deliveries = [] as OrderItemDelivery[];
  customVariationTotalQuantity: number;
  updating = false;
  loadingSummaryData = false;

  isCollapsed = true;

  purchaseResult: { added: boolean; updated: boolean; deleted: boolean, hasError: boolean } = {
    added: false,
    updated: false,
    deleted: false,
    hasError: false,
  };

  noAvailableShippingMethods = false;
  private alive = true;

  @Input() product: Product;
  @Input() orderType: string;

  _addressList: Address[] = [];
  _order: Order;

  @Output() changed = new EventEmitter<OrderItem>();

  constructor(
    private authService: AuthService,
    private orderItemService: OrderItemService,
    private paymentService: PaymentService,
    private productService: ProductService,
    private router: Router,
    private mobileCheck: MobileCheckServiceService,
    private orderService: OrderService,
    private toastr: ToastrService,
    private configService: ConfigService
  ) {}






  ngOnInit(): void {

    const activeOrderRx = this.orderType === ECOMMERCE_TYPE ? 'activeODOrder' : 'activeBWOrder';

    this.orderService.toRx(activeOrderRx).pipe(takeWhile(() => this.alive)).subscribe((order) => {
      if (!order || !order.loaded) {
        return;
      }

      if (!this._order || !this._order.isTheSame(order)) {
        this.resetOrderItem();
      }
      this._order = order;
      this._addressList = [...this._order.findAllOrderAddresses()];
      this.setupOrderItem();
    });

  }

  resetOrderItem() {
    this._orderItem = null;
    this._originalOrderItem = null;
  }

  setupOrderItem() {
    if (!this.product) {
      return;
    }

    if (!this._order) {
      return;
    }

    if (isEmptyArray(this._addressList)) {
      return;
    }

    let orderItem = this._order.findOrderItemByProduct(this.product);
    if (!orderItem) {
      // create a new order item
      orderItem = this._order.createEmptyOrderItemForProduct(this.product);
    }

    // check if order item deliveries  match  to the address list
    // remove  deliveries that are not in the address list
    const initialDeliveries = orderItem.deliveries.filter(d => {
      const address = this._addressList.find(a => a.uniqueAddressId === d.uniqueAddressId);
      return !!address;
    });

    const deliveries: OrderItemDelivery[] = [];
    for (const address of this._addressList) {
      const deliveryIdx  =  initialDeliveries.findIndex(d =>  d.uniqueAddressId === address.uniqueAddressId);
      if (deliveryIdx === - 1) {
        const newDelivery = new OrderItemDelivery({
          addr_id: address.id,
          type: 'SHIPPING',
          method: '',
          quantity: 0,
          data: {},
        });

        newDelivery.setAddress(
          address,
          this.authService.user,
          false,
          {canOrder: this.authService.canOrder, isEmployee: true}
        );
        deliveries.push(newDelivery);
      } else {
        // remove from initial deliveries and add to deliveries
        const delivery = initialDeliveries.splice(deliveryIdx, 1)[0];
        deliveries.push(delivery);
      }
    }

    // put the rest of deliveries to the end of the list
    deliveries.push(...initialDeliveries);


    // sort deliveries by address list
    const sortedDeliveries: OrderItemDelivery[] = [];
    for (const address of this._addressList) {
      const deliveryIdx  =  deliveries.findIndex(d =>  d.addr_id === address.id && d.address_type === address.address_type);
      if (deliveryIdx !== -1) {
        const delivery  =  deliveries.splice(deliveryIdx, 1)[0];
        sortedDeliveries.push(delivery);
      }
    }

    orderItem.deliveries = sortedDeliveries;
    orderItem.resetOriginalDeliveries();

    this._orderItem = cloneDeep(orderItem);
    this._originalOrderItem = cloneDeep(this._orderItem);

    this.computeTotalQuantity();
  }

  ngOnDestroy(): void {
    this.alive = false;
    this.resetOrderItem();
  }


  initProductUpdate() {
    // if (this.viewType === DETAILED_ITEM) {
    //   this.productService.populateProduct(this.product);
    // }

    this.productService.updatedProduct
      .pipe(takeWhile(() => this.alive))
      .subscribe((product) => {
        if (this.product && this._orderItem) {
          if (product && this._orderItem.product.id === product.id) {
            this.product = product;
            this._orderItem.product = product;
          }
        }
      });
  }

  onImageClicked() {
    this.router.navigate([this.product.productURL]);
  }

  onOrderItemChanged(orderItem: OrderItem) {
    if (orderItem) {
      this._orderItem = new OrderItem(orderItem);
      this._orderItem.changed = true;
      this._orderItem.computeAvailableAddresses(this._order.isWSLR);
      this.orderItemService.prefetchCapexForEntity(this._orderItem);

      this.noAvailableShippingMethods = this._orderItem.deliveries.some((delivery) => !delivery.method);
    }
  }

  onSaveWithDelay() {
    setTimeout(() => {
      this.saveChanges();
    }, 100);
  }



  updateShippingMethod(event, index) {
    if (event) {
      this.deliveries[index].method = event.method;
      this._orderItem.updateDelivery(
        this.deliveries[index],
        this._order.isOnDemand
      );
    }
  }

  updateAllocationGroup(event, index) {}

  onInternalOrderChanged(newValue, index) {
    const value = !!newValue ? newValue.trim() : '';
    if (this._order.isWBSDefined) {
      this.deliveries[index].wbs = value;
    } else {
      this.deliveries[index].internal_order = value;
    }
    this._orderItem.updateDelivery(
      this.deliveries[index],
      this._order.isOnDemand
    );
  }

  onInternalOrderReset(index) {
    this.deliveries[index].wbs = '';
    this.deliveries[index].internal_order = '';
    this._orderItem.updateDelivery(
      this.deliveries[index],
      this._order.isOnDemand
    );
  }

  private clearSaveResult(): void {
    this.purchaseResult = { added: false, updated: false, deleted: false, hasError: false };
  }



  get orderItem(): OrderItem {
    if (!this._order || !this._order.loaded) {
      return null;
    }

    return this._orderItem;
  }

  get orderedQuantity(): number {
    return this._orderItem?.quantity || 0;
  }

  get canBuyProduct(): boolean {

    return true;
  }

  get canSeeFavorites(): boolean {
    return this.authService.canSeeFavorites;
  }

  get shopLabel(): string {
    if (this._orderItem && this._orderItem.id > 0) {
      return `(${this._orderItem.quantity}) Update`;
    }
    return this.canBuyProduct ? 'Quick Shop' : 'Out Of Stock';
  }

  get orderItemUpdating(): boolean {
    return this.orderItemService.updatingOrderForProductId === this.product.id;
  }

  get hasFavorites(): boolean {
    return this.product && this.product.isFavorite;
  }

  get showMinOrder(): boolean {
    if (!this.product.isBuyingWindow) {
      return false;
    }

    if (this.product.hasVariations) {
      return (
        this.product.getGroupMinQuantity() > 0 ||
        this.product.variationsMaxQuantity > 0 ||
        this.product.has_inventory
      );
    }

    return true;
  }

  get isShopHidden(): boolean {
    if (this.mobile) {
      return true;
    }
    return !this.authService.canEditOrder(this._order) || this._order.limited;
  }

  get mobile(): boolean {
    return this.mobileCheck.mobile;
  }

  get imageUrl(): string {
    return this.product.thumbnail;
  }

  get isProductRecommended(): boolean {
    return this.orderItemService.isProductRecommended(
      this.product,
      this._order
    );
  }

  // in case of variations  - get first sku  that contains combined quantity
  get variationSKU(): SKU {
    return this.product.hasVariations ? this.product.firstSku : null;
  }


  get canOrder(): boolean {
    return this.authService.canOrder;
  }

  get canPurchase(): boolean {
    if (this.noAvailableShippingMethods) {
      return false; // cannot save order item when shipping method is not defined
    }

    if (this.isDisabled) {
      return false;
    }

    return (
      !this.paymentService.hasFailedTransactions &&
      this.orderService.canPurchase(this._order, this._orderItem)
    );
  }


  private calcAvailableShipmentMethods() {
    this.deliveries = this._orderItem.deliveries;

    const availableShippingMethods = this.product.shipping_methods;

    if (isEmptyArray(availableShippingMethods)) {
      this.deliveries.map((x) => {
        x.method = null;
        return x;
      });
      return;
    }

    this.shippingMethods = availableShippingMethods;
  }

  private computeTotalQuantity() {
    this._orderItem.quantity = this.getTotalQty();

    if (!this.product.hasVariations) {
      this._orderItem.skus[0].quantity = this._orderItem.quantity;
    }

    if (this._orderItem.product.isCustomizable) {
      this.customVariationTotalQuantity = 0;
      this._orderItem.skus.forEach((sku: OrderItemSku) => {
        this.customVariationTotalQuantity += sku.quantity;
      });
    }
  }

  private getTotalQty() {
    return this._orderItem.deliveries.reduce(
      (total, delivery) => total + Number(delivery.quantity),
      0
    );
  }

  saveWithDelay() {
    setTimeout(() => {
      this.saveChanges();
    }, 100);
  }


  get isVariationProduct(): boolean {
    return this.product.hasVariations;
  }


  get isEmptyAddressList(): boolean {
    return isEmptyArray(this._addressList);
  }

  get orderItemCurrency(): Currency {
    return this._originalOrderItem.product.currency;
  }

  saveChanges() {
    this.clearSaveResult();


    const copiedOrderItem = cloneDeep(this._orderItem);
    // remove deliveries with empty shipping method
    copiedOrderItem.deliveries = copiedOrderItem.deliveries.filter(
      (delivery) => delivery.method !== ''
    );

    if (isEmptyArray(copiedOrderItem.deliveries)) {
      this.toastr.warning('Please set address and  shipping quantity', 'Warning');
      return;
    }

    // filter out  deliveries with zero quantity and id = 0
    copiedOrderItem.deliveries = copiedOrderItem.deliveries.filter((delivery) => {
      if (delivery.quantity === 0 && !delivery.id) {
        // remove delivery with zero quantity and delivery id  is not set
        return false;
      }
      return true;
    });


    const errorMsg  =  validateOrderItem(copiedOrderItem);
    if (errorMsg) {
      this.toastr.warning(errorMsg, 'Warning');
      return;
    }

    const internalOrderValidationMsg  =  validateWbsForUnique(this._order, this._orderItem, this.configService.features);
    if (!!internalOrderValidationMsg) {
      this.toastr.warning(internalOrderValidationMsg, 'Warning');
      return;
    }

    const isNotSameCurrency = this._order.items.some(orderItem =>
      !isEqual(orderItem.product.currency, copiedOrderItem.product.currency)
    );

    if (isNotSameCurrency) {
      this.toastr.warning('This item is not in the same currency. Please create a new cart');
      return;
    }

    this.clearSaveResult();
    this.orderItemService.saveOrderItem(this._order, copiedOrderItem, false, this._originalOrderItem).subscribe(result => {
      if (result) {
        this.purchaseResult = result.purchaseResult;

        if (this.purchaseResult.hasError) {
          return;
        }

        if (result.orderItem) {
          this._orderItem = result.orderItem;
          this._orderItem.changed = false;
          this._originalOrderItem = new OrderItem(this._orderItem);
          this.changed.emit(null);
        } else {
          this._orderItem = new OrderItem(this._originalOrderItem);
        }

        if (result.order) {
          this._order = result.order;
        }
        this.product = this._orderItem.product;
        this.computeProductPrice();
        this.computeTotalCost();

      } else {
        this._orderItem = new OrderItem(this._originalOrderItem);
      }
    });
  }

  private computeProductPrice() {
    // this.productPrice = this._orderItem.getProductPrice();
  }


  private computeTotalCost() {
    // this.totalCost  = this._orderItem.financials_total.subtotal;
    // // calculate discount if any
    // this.totalCost = ProductService.computeDiscountPrice(this.totalCost, this.product, this._orderItem.hasTrimesterBudget);
    //

  }


  toggleCollapsed() {
    this.isCollapsed = !this.isCollapsed;
  }


  get isDisabled(): boolean {
    if (this.isItemCut) {
      return true;
    }

    if (this.isVariationProduct) {
      return true;
    }

    if (this.isOutOfStock) {
      return true;
    }

    return !this.orderHasTheSameCurrency;
  }

  get isOutOfStock(): boolean {
    if (this.product.inventoryType) {
      return this.product.inStockQuantity === 0;
    }

    return false;
  }

  get orderHasTheSameCurrency(): boolean {
    if  (!this._order.orderCurrency) {
      return  true;
    }

    return this._order.orderCurrency?.symbol === this.product.currency.symbol;
  }

  get disabledTooltip(): string {
    if (!this.orderHasTheSameCurrency) {
      return 'Currency is different';
    }

    if (this.isVariationProduct) {
      return 'Product has variations';
    }

    if (this.isOutOfStock) {
      return 'Product is out of stock';
    }

    if (this.noAvailableShippingMethods) {
      return 'Shipping method is not available';
    }

    return '';
  }

  onDeliveryChanged() {
    this._orderItem.changed = true;
    this.orderItemService.orderWithIdHasChanges = this._order.id;
  }

  get isItemCut(): boolean {
    return this.product?.item_cut ? this.product.item_cut : false;
  }

}
